import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MessageSquare, X, Info, ChevronDown, ChevronUp, FileUp, Send, CheckCircle, DollarSign, FileText } from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schemaData';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import WaitTimesDialog from '../components/WaitTimesDialog';
import './Bookings.css';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const Bookings = () => {
  const breadcrumbs = breadcrumbSchema([
    { name: "Home", url: "https://www.clinicalminds.net/" },
    { name: "Bookings", url: "https://www.clinicalminds.net/bookings" }
  ]);
  const [showMhtpModal, setShowMhtpModal] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [showFeesModal, setShowFeesModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [expandedStep3, setExpandedStep3] = useState(null);

  // Upload form state
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [formData, setFormData] = useState({
    patientName: '',
    referringPractitioner: '',
    contactEmail: '',
    file: null
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, file: e.target.files[0] }));
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setUploading(true);
    setUploadError('');
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('patient_name', formData.patientName);
      formDataToSend.append('referring_practitioner', formData.referringPractitioner);
      formDataToSend.append('contact_email', formData.contactEmail);
      if (formData.file) formDataToSend.append('file', formData.file);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/referral/upload`, { method: 'POST', body: formDataToSend });
      const data = await response.json();
      if (response.ok && data.success) {
        setUploadSuccess(true);
        setTimeout(() => {
          setShowUploadModal(false);
          setUploadSuccess(false);
          setFormData({ patientName: '', referringPractitioner: '', contactEmail: '', file: null });
        }, 3000);
      } else {
        setUploadError(data.detail || 'Upload failed. Please try again.');
      }
    } catch (error) {
      setUploadError('Network error. Please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const toggleStep3Section = (section) => {
    setExpandedStep3(expandedStep3 === section ? null : section);
  };

  return (
    <div className="page-bookings">
      <SEO
        title="Request an Appointment | Clinical Psychologist (Registrar) Coffs Harbour"
        description="Request a psychology or assessment appointment with Lachlan Beaton, Clinical Psychologist (Registrar). Coffs Harbour, visiting clinics, or telehealth across Australia."
        canonicalUrl="https://www.clinicalminds.net/bookings"
        schemaData={breadcrumbs}
      />
      <ScrollIndicator />

      {/* Hero Section */}
      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Bookings</span>
          <h1 className="heading-hero">Request an Appointment</h1>
          <p className="hero-subline">Tell us what you need and we'll be in touch within 24 hours</p>
        </div>
      </section>

      {/* Booking Request Section */}
      <section className="bookings-content">
        <div className="container">
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            {/* Availability lives on the homepage strip (round 6). Here the link
                opens the same strip in a pop-up, only when tapped (round 6b). */}
            <div className="body-medium" style={{ marginBottom: '1rem' }} data-testid="bookings-wait-times-link">
              <WaitTimesDialog />
            </div>
            <p className="body-medium" style={{ marginBottom: '2.5rem', lineHeight: 1.8 }}>
              Demand for our services is currently very high, so rather than showing a live calendar, we ask you to send a booking request. We'll contact you within 24 hours to talk through fit and find the next available time.
            </p>

            <div className="booking-steps" data-testid="booking-steps">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content-full">
                  <h3 className="heading-3">Send your request</h3>
                  <p className="body-medium">
                    Use the form below, or phone <a href="tel:0266002710" className="inline-link">02 6600 2710</a>, SMS <a href="sms:0415220944" className="inline-link">0415 220 944</a>, or email <a href="mailto:contact@clinicalminds.net" className="inline-link">contact@clinicalminds.net</a>.
                  </p>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content-full">
                  <h3 className="heading-3">We'll call you</h3>
                  <p className="body-medium">
                    We'll call you within 24 hours to make sure we're the right service for you and discuss availability.
                  </p>
                </div>
              </div>

              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content-full">
                  <h3 className="heading-3">Confirmation</h3>
                  <p className="body-medium">
                    Once confirmed, you'll receive confirmation by email and SMS, plus intake and consent forms to complete online.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              <Link to="/contact?enquiry=Assessment" className="btn-primary" data-testid="booking-request-cta">
                Send a Booking Request
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Information Section */}
      <section className="bookings-content" style={{ paddingTop: '3rem', paddingBottom: '3rem', background: '#ffffff' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <h2 className="heading-2" style={{ color: 'var(--blue-primary)', marginBottom: '0.5rem' }}>
              Need More Information?
            </h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
              Learn about what happens next and important details about your appointment
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 450px), 1fr))', gap: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Card 1: What Happens After I Book */}
            <div className="card" style={{ borderTop: '4px solid var(--blue-primary)', height: 'fit-content' }}>
              <h2 className="heading-2 how-to-book-title">What Happens After I Book?</h2>
              <div className="booking-steps">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-content-full">
                    <h3 className="heading-3">We'll Confirm and Guide You</h3>
                    <p className="body-medium" style={{ marginBottom: '1rem' }}>
                      We'll contact you within 24 hours to confirm your appointment and send you everything you need.
                    </p>
                    <div className="step3-expandable" id="initial-appointments">
                      <button className="step3-toggle" onClick={() => toggleStep3Section('initial')}>
                        <span className="step3-toggle-title">Initial Appointments</span>
                        {expandedStep3 === 'initial' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      {expandedStep3 === 'initial' && (
                        <div className="step3-content">
                          <p className="body-medium" style={{ marginBottom: '0.75rem' }}>If this is your first appointment:</p>
                          <ul className="step3-bullet-list">
                            <li>We will contact you within 24 hours to ensure we are the right service for you</li>
                            <li>Once confirmed, we'll book your appointment</li>
                            <li>You will receive confirmation by email and SMS</li>
                            <li>We will email you intake and consent forms that you can fill out online</li>
                            <li>If you can't fill out a form online, you can{' '}
                              <a href="/Clinical_Minds_New_Client_Intake_and_Consent.pdf" target="_blank" rel="noopener noreferrer" className="inline-link">download it here</a> and bring it with you to your first appointment
                            </li>
                            <li>A reminder will be sent via SMS and email 3 days before your session</li>
                            <li>Please respond to the SMS to confirm you can still make it</li>
                            <li>If you need to change or cancel, please{' '}
                              <a href="tel:0266002710" className="inline-link">contact us</a> at least 24 hours beforehand to avoid a late fee
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="step3-expandable" id="follow-up">
                      <button className="step3-toggle" onClick={() => toggleStep3Section('followup')}>
                        <span className="step3-toggle-title">Follow-up Appointments</span>
                        {expandedStep3 === 'followup' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      {expandedStep3 === 'followup' && (
                        <div className="step3-content">
                          <p className="body-medium" style={{ marginBottom: '0.75rem' }}>For follow-up appointments:</p>
                          <ul className="step3-bullet-list">
                            <li>We will book your preferred time</li>
                            <li>Confirmation will be sent via SMS and email</li>
                            <li>A reminder will be sent via SMS and email 3 days before your session</li>
                            <li>Please respond to the SMS to confirm you can still make it</li>
                            <li>If you need to change or cancel, please{' '}
                              <a href="tel:0266002710" className="inline-link">contact us</a> at least 24 hours beforehand to avoid a late fee
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number">2</div>
                  <div>
                    <h3 className="heading-3">Attend Your Appointment</h3>
                    <p className="body-medium">
                      You will receive a reminder by SMS and email a few days before, and you can find our <a href="/contact" className="inline-link">location details here</a>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Important Information */}
            <div className="card" style={{ borderTop: '4px solid var(--blue-secondary)', height: 'fit-content' }}>
              <h2 className="heading-2 how-to-book-title">Important Information</h2>
              <div className="steps">
                <div className="step-item" id="referral">
                  <div className="step-icon-wrapper"><FileText size={24} strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="heading-3">Referral / Mental Health Treatment Plan</h3>
                    <p className="body-medium">
                      If you wish to claim a Medicare rebate, you will need a referral/Mental Health Treatment Plan from your GP. For more information see our <a href="/faqs" className="inline-link">FAQs</a> or click the button below.
                    </p>
                    <button className="modal-button" onClick={() => setShowMhtpModal(true)}>
                      <Info size={16} /> Learn About Mental Health Treatment Plans
                    </button>
                  </div>
                </div>
                <div className="step-item" id="fees" style={{ marginTop: '2rem' }}>
                  <div className="step-icon-wrapper"><DollarSign size={24} strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="heading-3">Fees and Payment</h3>
                    <p className="body-medium">
                      Check out our <a href="/faqs" className="inline-link">FAQ page</a> for more information or click the button below for detailed fee information.
                    </p>
                    <button className="modal-button" onClick={() => setShowFeesModal(true)}>
                      <Info size={16} /> View Fee Details
                    </button>
                    <p className="body-small" style={{ marginTop: '0.75rem', color: '#666' }}>
                      View our <a href="/terms-and-conditions" className="inline-link" style={{ fontSize: '0.875rem' }}>Terms and Conditions</a>
                    </p>
                  </div>
                </div>
                <div className="step-item" id="upload" style={{ marginTop: '2rem' }}>
                  <div className="step-icon-wrapper"><FileUp size={24} strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="heading-3">Have a Referral or Document to Upload?</h3>
                    <p className="body-medium">
                      We will send you a link as part of the intake process but if you are a GP and wish to send it to us securely, below is a link.
                    </p>
                    <button onClick={() => setShowUploadModal(true)} className="modal-button">
                      <FileUp size={16} /> Upload Referral
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mental Health Treatment Plan Modal */}
      {showMhtpModal && (
        <div className="modal-overlay" onClick={() => setShowMhtpModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="heading-2">Mental Health Treatment Plan</h3>
              <button className="modal-close" onClick={() => setShowMhtpModal(false)}><X size={24} /></button>
            </div>
            <div className="modal-body">
              <h4 className="heading-3">What is a Mental Health Treatment Plan?</h4>
              <p className="body-medium">
                A Mental Health Treatment Plan is a document created by a general practitioner (GP) in Australia that outlines a patient's mental health needs, goals, and a treatment plan. It allows eligible patients to access Medicare rebates for up to 10 individual psychological sessions per calendar year. After six sessions, we write to your GP to update them on your progress. At this point, you will need to see your GP again, so they can request your final 4 sessions for the year.
              </p>
              <h4 className="heading-3">How can I arrange a Mental Health Treatment Plan?</h4>
              <p className="body-medium">
                Visit your GP and explain that you would like to see a psychologist to work on your mental health. Your GP will assess your needs and, if appropriate, provide a Mental Health Treatment Plan. You need this plan to be eligible for the Medicare rebate.
              </p>
              <h4 className="heading-3">Where to Send It</h4>
              <p className="body-medium">
                Please email the Mental Health Treatment plan to us at{' '}
                <a href="mailto:contact@clinicalminds.net" className="inline-link">contact@clinicalminds.net</a>{' '}
                prior to your session or you can{' '}
                <button
                  onClick={() => { setShowMhtpModal(false); setShowUploadModal(true); }}
                  style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >upload it here</button>.
              </p>
              <h4 className="heading-3">Medicare Rebate</h4>
              <p className="body-medium">
                With a Mental Health Treatment Plan, you can claim a Medicare rebate of $101.55 per session.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Fees Modal */}
      {showFeesModal && (
        <div className="modal-overlay" onClick={() => setShowFeesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="heading-2">Fee Information</h3>
              <button className="modal-close" onClick={() => setShowFeesModal(false)}><X size={24} /></button>
            </div>
            <div className="modal-body">
              <h4 className="heading-3">Session Fees</h4>
              <p className="body-medium">
                All individual sessions are <strong>$220</strong>. Psychological Assessment fees (ADHD, Autism Spectrum Disorder, Learning Difficulties, Cognitive Testing) will be discussed prior to you booking, as it will depend on your needs. There is no Medicare rebate for Psychological Assessments.
              </p>
              <h4 className="heading-3">Medicare Rebate</h4>
              <p className="body-medium">
                With a Mental Health Treatment Plan from your GP, you can claim a Medicare rebate of <strong>$101.55</strong> per session. This means your out-of-pocket cost is approximately <strong>$118.45</strong>.
              </p>
              <h4 className="heading-3">Payment Options</h4>
              <ul className="fee-list">
                <li className="body-medium">Credit card (online or in person)</li>
                <li className="body-medium">EFTPOS (in person)</li>
                <li className="body-medium">Phone payment processing</li>
              </ul>
              <h4 className="heading-3">Medicare Processing</h4>
              <p className="body-medium">
                Medicare rebates are deposited directly into your bank account, overnight, if paying for your session via card in person or online. We can submit rebates on your behalf (usually refunded within 24 hours) or process them instantly if you use an EFTPOS debit card at our clinic.
              </p>
              <h4 className="heading-3">Private Health Insurance</h4>
              <p className="body-medium">
                Private health insurance claims can be made online after your appointment, provided your cover extends to Psychology.
              </p>
              <h4 className="heading-3">NDIS</h4>
              <p className="body-medium">
                We accept NDIS clients, and will work with you to ensure our sessions work toward meeting your goals. We are able to invoice the NDIS or your plan manager.
              </p>
            </div>
          </div>
        </div>
      )}

      <CrisisSupport />

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="heading-2">Upload Referral</h2>
              <button className="modal-close-btn" onClick={() => setShowUploadModal(false)} aria-label="Close modal">x</button>
            </div>
            {uploadSuccess ? (
              <div className="upload-success">
                <CheckCircle size={64} className="success-icon" />
                <h3 className="heading-3">Referral Uploaded Successfully!</h3>
                <p className="body-medium">Thank you. We'll contact the patient within 24-48 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleFileUpload} className="modal-body-form">
                {formsOff && <PreviewFormNotice />}
                <div className="privacy-notice-top">
                  <p className="body-small" style={{ fontStyle: 'italic', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                    We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy Policy</a>.
                  </p>
                </div>
                <div className="form-group">
                  <label htmlFor="patientName" className="form-label">Patient Name *</label>
                  <input type="text" id="patientName" name="patientName" className="form-input" value={formData.patientName} onChange={handleInputChange} required disabled={uploading} />
                </div>
                <div className="form-group">
                  <label htmlFor="referringPractitioner" className="form-label">Referring Practitioner *</label>
                  <input type="text" id="referringPractitioner" name="referringPractitioner" className="form-input" value={formData.referringPractitioner} onChange={handleInputChange} required disabled={uploading} />
                </div>
                <div className="form-group">
                  <label htmlFor="contactEmail" className="form-label">Contact Email *</label>
                  <input type="email" id="contactEmail" name="contactEmail" className="form-input" value={formData.contactEmail} onChange={handleInputChange} required disabled={uploading} />
                </div>
                <div className="form-group">
                  <label htmlFor="file" className="form-label">Referral Document (Optional)</label>
                  <input type="file" id="file" name="file" className="form-input file-input" onChange={handleFileChange} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" disabled={uploading} />
                  <p className="body-small form-help">Accepted formats: PDF, Word, or images (max 10MB)</p>
                </div>
                {uploadError && <div className="upload-error"><p className="body-medium">{uploadError}</p></div>}
                <button type="submit" className="btn btn-primary btn-submit" disabled={uploading || formsOff}>
                  {uploading ? (<><span className="spinner"></span>Uploading...</>) : (<><Send size={20} />Submit Referral</>)}
                </button>
                <p className="body-small consent-text">
                  By submitting this form, you confirm that you have read and agree to our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
